import { Clerk } from '@clerk/clerk-js';

const ENV = (import.meta as any).env;

// Extend Window interface to include Clerk instance from ClerkProvider
declare global {
  interface Window {
    Clerk?: Clerk;
  }
}

/**
 * Get the Clerk client instance from ClerkProvider (via window.Clerk)
 * This accesses the same instance used by React hooks, ensuring session state is shared.
 * Falls back to null if ClerkProvider hasn't initialized yet.
 */
export function getClerkClient(): Clerk | null {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return null;
  }

  // Check if Clerk is configured
  const publishableKey = ENV?.VITE_APP_CLERK_PUBLISHABLE_KEY;
  if (!publishableKey) {
    return null;
  }

  // Access the Clerk instance created by ClerkProvider
  // This is the same instance used by useAuth, useUser, etc.
  if (window.Clerk) {
    return window.Clerk;
  }

  // ClerkProvider hasn't initialized yet
  return null;
}

/**
 * Wait for the Clerk client to be available
 * ClerkProvider initializes asynchronously, so we may need to wait
 */
export async function loadClerkClient(): Promise<Clerk | null> {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return null;
  }

  // Check if Clerk is configured
  const publishableKey = ENV?.VITE_APP_CLERK_PUBLISHABLE_KEY;
  if (!publishableKey) {
    return null;
  }

  // If Clerk is already available, return it
  if (window.Clerk) {
    return window.Clerk;
  }

  // Wait for Clerk to be initialized by ClerkProvider (max 5 seconds)
  const maxWaitMs = 5000;
  const checkIntervalMs = 100;
  let waited = 0;

  while (!window.Clerk && waited < maxWaitMs) {
    await new Promise((resolve) => setTimeout(resolve, checkIntervalMs));
    waited += checkIntervalMs;
  }

  if (window.Clerk) {
    return window.Clerk;
  }

  console.warn('[clerkClient] Clerk not available after waiting');
  return null;
}

/**
 * Get the current session token from Clerk
 * Uses the Clerk instance from ClerkProvider to ensure session state is correct.
 * Returns null if Clerk is not configured or no session exists.
 */
export async function getClerkToken(): Promise<string | null> {
  const client = await loadClerkClient();
  if (!client) {
    return null;
  }

  try {
    const session = client.session;
    if (!session) {
      // No active session - user not logged in
      return null;
    }

    const token = await session.getToken();
    return token;
  } catch (error) {
    console.error('[clerkClient] Failed to get token:', error);
    return null;
  }
}

/**
 * Check if Clerk is configured in the environment
 */
export function isClerkConfigured(): boolean {
  return Boolean(ENV?.VITE_APP_CLERK_PUBLISHABLE_KEY);
}
